import { useState, useEffect } from 'react';
import axios from 'axios';
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';  // ← Import direct de la fonction
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';

interface Product {
  id: string;
  sku: string;
  name: string;
  shortName: string;
  category: string;
  unit: string;
  barcode: string;
  supplier: string;
  purchasePrice: number;
  salePrice: number;
  vatPurchase: number;
  vatSale: number;
  stockQuantity: number;
  stockMin: number;
  stockMax: number;
  location: string;
  image: string;
  weight?: number;
  volume?: number;
}

interface ProductsListProps {
  onEdit: (product: Product) => void;
  onAdd: () => void;
}

export default function ProductsList({ onEdit, onAdd }: ProductsListProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelProduct, setLabelProduct] = useState<Product | null>(null);
  const [movements, setMovements] = useState<any[]>([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  // Chargement des produits
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const response = await getResources('produits');
        setProducts(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Catégories dynamiques
  const categories = ['Tous', ...Array.from(new Set(products.map(p => p.category)))].sort();

  const filteredProducts = products.filter(product => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.barcode.includes(searchTerm);

    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;

    const matchesStock =
      stockFilter === 'all' ||
      (stockFilter === 'low' && product.stockQuantity <= product.stockMin) ||
      (stockFilter === 'out' && product.stockQuantity === 0) ||
      (stockFilter === 'ok' && product.stockQuantity > product.stockMin);

    return matchesSearch && matchesCategory && matchesStock;
  });

  const totalValue = products.reduce((sum, p) => sum + (p.stockQuantity * p.purchasePrice), 0);
  const lowStockCount = products.filter(p => p.stockQuantity <= p.stockMin).length;
  const outOfStockCount = products.filter(p => p.stockQuantity === 0).length;

  const getStockStatus = (product: Product) => {
    if (product.stockQuantity === 0) return { label: 'Rupture', color: 'bg-red-100 text-red-800' };
    if (product.stockQuantity <= product.stockMin) return { label: 'Stock Faible', color: 'bg-orange-100 text-orange-800' };
    if (product.stockQuantity >= product.stockMax) return { label: 'Surstock', color: 'bg-purple-100 text-purple-800' };
    return { label: 'Normal', color: 'bg-green-100 text-green-800' };
  };

  const handleViewHistory = async (product: Product) => {
    setSelectedProduct(product);
    setLoadingMovements(true);
    setShowHistoryModal(true);

    try {
      // Remplace par ton endpoint réel Laravel
      const response = await getResources(`produits/${product.id}/mouvements`);
      setMovements(response.data); // suppose que l'API retourne un tableau de mouvements
      console.log( 'id', product ,'mon mouvement' ,movements)
    } catch (error) {
      console.error('Erreur lors du chargement des mouvements:', error);
      setMovements([]); // en cas d'erreur, on affiche une liste vide
    } finally {
      setLoadingMovements(false);
    }
  };

  const handlePrintLabel = (product: Product) => {
    setLabelProduct(product);
    setShowLabelModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = async (format: 'excel' | 'pdf' | 'csv') => {
    setIsExporting(true);

    const data = filteredProducts.map(p => ({
      SKU: p.sku,
      Désignation: p.shortName,
      Catégorie: p.category,
      Unité: p.unit,
      Stock: p.stockQuantity,
      'Stock Min': p.stockMin,
      'Stock Max': p.stockMax,
      'Prix Achat HT': parseFloat(p.purchasePrice.toFixed(4)),
      'Prix Vente HT': parseFloat(p.salePrice.toFixed(4)),
      Emplacement: p.location,
      Fournisseur: p.supplier,
    }));

    const filename = `produits_${new Date().toISOString().split('T')[0]}`;

    try {
      if (format === 'csv') {
        // === CSV ===
        const headers = Object.keys(data[0] || {}).join(';');
        const rows = data.map(row => 
          Object.values(row)
            .map(value => `"${String(value).replace(/"/g, '""')}"`)
            .join(';')
        ).join('\n');
        
        const csvContent = headers + '\n' + rows;
        const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }

      else if (format === 'excel') {
        // === EXCEL (.xlsx) ===
        const ws = XLSXUtils.json_to_sheet(data);
        const wb = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(wb, ws, 'Produits');
        
        // Ajuster la largeur des colonnes
        const colWidths = Object.keys(data[0] || {}).map(() => ({ wch: 15 }));
        ws['!cols'] = colWidths;

        const excelBuffer = XLSXWrite(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      }

      else if (format === 'pdf') {
        // === PDF ===
        const doc = new jsPDF('l', 'mm', 'a4'); // paysage pour plus de colonnes

        doc.setFontSize(18);
        doc.text('Liste des Produits', 14, 20);

        doc.setFontSize(10);
        doc.text(`Exporté le ${new Date().toLocaleDateString('fr-FR')}`, 14, 28);
        doc.text(`${filteredProducts.length} produit(s)`, 14, 34);

        // Utilise la fonction importée directement
        autoTable(doc, {
          head: [Object.keys(data[0] || {})],
          body: data.map(row => Object.values(row)),
          startY: 45,
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [243, 244, 246] },
          columnStyles: {
            0: { cellWidth: 30 }, // SKU
            1: { cellWidth: 60 }, // Désignation
            7: { halign: 'right' }, // Prix Achat (index 7)
            8: { halign: 'right' }, // Prix Vente (index 8)
            4: { halign: 'center' }, // Stock
          },
        });

        doc.save(`${filename}.pdf`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'export', error);
      alert('Une erreur est survenue lors de l\'export');
    } finally {
      setIsExporting(false);
      setShowExportModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-spin w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
        <p className="text-gray-600 text-lg">Chargement des produits...</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">

        {/* === CARTES DE STATISTIQUES === */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Produits</p>
                <p className="text-3xl font-bold mt-2">{products.length}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-product-hunt-line text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Valeur du Stock</p>
                <p className="text-3xl font-bold mt-2">{totalValue.toFixed(2)} €</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-money-euro-circle-line text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Stock Faible</p>
                <p className="text-3xl font-bold mt-2">{lowStockCount}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-alarm-warning-line text-2xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Ruptures</p>
                <p className="text-3xl font-bold mt-2">{outOfStockCount}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-close-circle-line text-2xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* === TABLEAU + FILTRES === */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Rechercher par nom, SKU ou code-barres..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer pr-8"
            >
              {categories.map(cat => (
                <option key={cat} value={cat === 'Tous' ? 'all' : cat}>{cat}</option>
              ))}
            </select>

            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer pr-8"
            >
              <option value="all">Tous les stocks</option>
              <option value="ok">Stock Normal</option>
              <option value="low">Stock Faible</option>
              <option value="out">Rupture</option>
            </select>

            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line"></i>
              Exporter
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {/* <th className="text-left py-4 px-4 font-semibold text-gray-700">Image</th> */}
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">SKU</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Désignation</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Catégorie</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Fournisseur</th>
                  <th className="text-right py-4 px-4 font-semibold text-gray-700">Prix Achat</th>
                  <th className="text-right py-4 px-4 font-semibold text-gray-700">Prix Vente</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Stock</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Emplacement</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Statut</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const status = getStockStatus(product);
                  return (
                    <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      {/* <td className="py-4 px-4">
                        <img
                          src={product.image}
                          alt={product.shortName}
                          className="w-12 h-12 object-cover rounded-lg shadow-sm"
                          onError={(e) => {
                            e.currentTarget.src = `https://via.placeholder.com/200x200/f3f4f6/9ca3af?text=${product.shortName?.[0] || 'P'}`;
                          }}
                        />
                      </td> */}
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm font-medium text-gray-900">{product.sku}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{product.shortName}</p>
                          <p className="text-sm text-gray-500">{product.unit}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {product.category}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-gray-700">{product.supplier}</td>
                      <td className="py-4 px-4 text-right font-medium text-gray-900">
                        {product.purchasePrice.toFixed(4)} €
                      </td>
                      <td className="py-4 px-4 text-right font-medium text-green-600">
                        {product.salePrice.toFixed(4)} €
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div>
                          <p className="font-bold text-gray-900">{product.stockQuantity}</p>
                          <p className="text-xs text-gray-500">Min: {product.stockMin} / Max: {product.stockMax}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm font-mono">
                          {product.location}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => onEdit(product)} title="Modifier" className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer">
                            <i className="ri-edit-line text-lg"></i>
                          </button>
                          <button onClick={() => handleViewHistory(product)} title="Historique" className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                            <i className="ri-history-line text-lg"></i>
                          </button>
                          {/* <button onClick={() => handlePrintLabel(product)} title="Étiquette" className="w-8 h-8 flex items-center justify-center text-green-600 hover:bg-green-50 rounded-lg transition-colors cursor-pointer">
                            <i className="ri-barcode-line text-lg"></i>
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">Aucun produit trouvé</p>
            </div>
          )}
        </div>

        {/* Modal Historique */}
        {showHistoryModal && selectedProduct && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Historique des mouvements
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedProduct.shortName} • SKU: {selectedProduct.sku}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowHistoryModal(false);
                    setMovements([]);
                    setLoadingMovements(false);
                  }}
                  className="w-10 h-10 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                >
                  <i className="ri-close-line text-2xl text-gray-500"></i>
                </button>
              </div>

              <div className="p-6">
                {/* Cartes récapitulatives */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg p-5 text-center">
                    <p className="text-blue-100 text-xs">Stock actuel</p>
                    <p className="text-3xl font-bold mt-1">{selectedProduct.stockQuantity}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg p-5 text-center">
                    <p className="text-green-100 text-xs">Valeur stock</p>
                    <p className="text-2xl font-bold mt-1">
                      {(selectedProduct.stockQuantity * selectedProduct.purchasePrice).toFixed(2)} €
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg p-5 text-center">
                    <p className="text-purple-100 text-xs">Emplacement</p>
                    <p className="text-xl font-bold mt-1">{selectedProduct.location}</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg p-5 text-center">
                    <p className="text-orange-100 text-xs">Total mouvements</p>
                    <p className="text-3xl font-bold mt-1">{movements.length}</p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white rounded-lg p-5 text-center">
                    <p className="text-gray-100 text-xs">Dernier mouvement</p>
                    <p className="text-lg font-bold mt-1">
                      {movements.length > 0
                        ? new Date(movements[0].date_mouvement).toLocaleDateString('fr-FR')
                        : '-'}
                    </p>
                  </div>
                </div>

                {/* Loader */}
                {loadingMovements && (
                  <div className="text-center py-16">
                    <div className="inline-block animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                    <p className="text-gray-600 mt-4 text-lg">Chargement des mouvements...</p>
                  </div>
                )}

                {/* Pas de mouvements */}
                {!loadingMovements && movements.length === 0 && (
                  <div className="text-center py-16 bg-gray-50 rounded-xl">
                    <i className="ri-inbox-line text-8xl text-gray-300 mb-4"></i>
                    <p className="text-xl text-gray-500">Aucun mouvement enregistré pour ce produit</p>
                  </div>
                )}

                {/* Tableau des mouvements */}
                {!loadingMovements && movements.length > 0 && (
                  <div className="overflow-x-auto border border-gray-200 rounded-xl">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-4 px-6 font-semibold text-gray-700">Date</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-700">Type</th>
                          <th className="text-center py-4 px-6 font-semibold text-gray-700">Qté</th>
                          <th className="text-right py-4 px-6 font-semibold text-gray-700">Prix unitaire</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-700">Document</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-700">Motif</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-700">Utilisateur</th>
                          <th className="text-left py-4 px-6 font-semibold text-gray-700">Emplacement</th>
                        </tr>
                      </thead>
                      <tbody>
                        {movements.map((m, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="py-4 px-6">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {new Date(m.date_mouvement).toLocaleDateString('fr-FR')}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(m.date_mouvement).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </td>

                            <td className="py-4 px-6">
                              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                m.type_mouvement === 'entrée' || m.type_mouvement === 'entree'
                                  ? 'bg-green-100 text-green-800'
                                  : m.type_mouvement === 'sortie'
                                  ? 'bg-red-100 text-red-800'
                                  : m.type_mouvement === 'transfert'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {m.type_mouvement?.charAt(0).toUpperCase() + m.type_mouvement?.slice(1)}
                              </span>
                            </td>

                            <td className="py-4 px-6 text-center">
                              <span className={`text-lg font-bold ${
                                m.quantite > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {m.quantite > 0 ? '+' : ''}{m.quantite}
                              </span>
                            </td>

                            <td className="py-4 px-6 text-right font-medium text-gray-900">
                              {m.prix_unitaire ? `${parseFloat(m.prix_unitaire).toFixed(4)} €` : '-'}
                            </td>

                            <td className="py-4 px-6">
                              <span className="text-sm font-medium text-blue-600">
                                {m.document_source} #{m.document_id || '-'}
                              </span>
                            </td>

                            <td className="py-4 px-6 text-gray-700">
                              {m.motif || '-'}
                            </td>

                            <td className="py-4 px-6">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-xs font-bold text-gray-700">
                                  {m.utilisateur?.name?.[0] || 'S'}
                                </div>
                                <span className="text-sm font-medium text-gray-900">
                                  {m.utilisateur?.name || 'Système'}
                                </span>
                              </div>
                            </td>

                            <td className="py-4 px-6 text-sm text-gray-600">
                              {m.emplacement_source?.code && m.emplacement_destination?.code
                                ? `${m.emplacement_source.code} → ${m.emplacement_destination.code}`
                                : m.emplacement_source?.code || m.emplacement_destination?.code || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal Export */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-xl font-bold text-gray-900">Exporter les Produits</h2>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  Choisissez le format d'export pour {filteredProducts.length} produit(s) :
                </p>
                <div className="space-y-3">
                  <button onClick={() => handleExport('excel')} disabled={isExporting}
                    className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50">
                    <i className="ri-file-excel-2-line text-2xl text-green-600"></i>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">Excel (.xlsx)</p>
                      <p className="text-sm text-gray-500">Fichier Excel avec toutes les données</p>
                    </div>
                  </button>
                  <button onClick={() => handleExport('pdf')} disabled={isExporting}
                    className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50">
                    <i className="ri-file-pdf-line text-2xl text-red-600"></i>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">PDF (.pdf)</p>
                      <p className="text-sm text-gray-500">Document PDF formaté</p>
                    </div>
                  </button>
                  <button onClick={() => handleExport('csv')} disabled={isExporting}
                    className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50">
                    <i className="ri-file-text-line text-2xl text-blue-600"></i>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">CSV (.csv)</p>
                      <p className="text-sm text-gray-500">Fichier CSV pour import</p>
                    </div>
                  </button>
                </div>
                {isExporting && (
                  <div className="mt-6 flex items-center justify-center gap-3 text-blue-600">
                    <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                    <span>Export en cours...</span>
                  </div>
                )}
              </div>
              <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
                <button onClick={() => setShowExportModal(false)} disabled={isExporting}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer disabled:opacity-50">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Étiquette */}
        {showLabelModal && labelProduct && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 print-label-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between print:hidden">
                <h3 className="text-xl font-bold text-gray-900">Étiquette Code-Barres</h3>
                <button onClick={() => setShowLabelModal(false)}
                  className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <div className="p-6">
                <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6">
                  <div className="max-w-md mx-auto text-center">
                    <h4 className="text-lg font-bold text-gray-900 mb-1">{labelProduct.name}</h4>
                    <p className="text-sm text-gray-600 mb-6">{labelProduct.category}</p>

                    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 inline-block">
                      <svg className="w-64 h-24" viewBox="0 0 260 100">
                        {/* Barres du code-barres */}
                        {[10,15,22,27,36,41,48,53,62,69,74,83,88,95,100,109,114,121,130,135,142,147,156,163,168,177,182,189].map((x, i) => (
                          <rect key={i} x={x} y="15" width={i % 3 === 0 ? 2 : 4} height="50" fill="black"/>
                        ))}
                        <text x="130" y="90" textAnchor="middle" fontSize="14" fill="black" fontFamily="monospace" fontWeight="bold">
                          {labelProduct.sku}
                        </text>
                      </svg>
                    </div>

                    <div className="grid grid-cols-2 gap-6 text-left text-sm">
                      <div><span className="text-gray-600">SKU :</span> <strong>{labelProduct.sku}</strong></div>
                      <div><span className="text-gray-600">Prix :</span> <strong>{labelProduct.salePrice.toFixed(2)} €</strong></div>
                      <div><span className="text-gray-600">Stock :</span> <strong>{labelProduct.stockQuantity} unités</strong></div>
                      <div><span className="text-gray-600">Emplacement :</span> <strong>{labelProduct.location}</strong></div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6 print:hidden">
                  <h5 className="font-semibold text-gray-900 mb-3">Options d'impression</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-700 mb-2">Nombre d'étiquettes</label>
                      <input type="number" min="1" max="100" defaultValue="1" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-2">Format</label>
                      <select className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 cursor-pointer">
                        <option>Standard (10x5 cm)</option>
                        <option>Petit (7x3 cm)</option>
                        <option>Grand (15x7 cm)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 print:hidden">
                  <button onClick={handlePrint}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer">
                    <i className="ri-printer-line text-lg"></i> Imprimer
                  </button>
                  <button onClick={() => setShowLabelModal(false)}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium cursor-pointer">
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}